import React, { useContext, useEffect, useRef, useState } from 'react';
import styles from './Login.module.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import eyeOpen from '../../../images/icon-eye-open.svg';
import eyeClose from '../../../images/icon-eye-close.svg';
import { useDispatch, useSelector } from 'react-redux';
import { getAuth, resetValidationMessage } from '../../../redux/auth/actions/auth.action';
import { Roles } from '../../../constants/constants';
import { AppContext } from '../../context/AppContextProvider';
import Loader from '../../common/loader/Loader';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import Toast from '../../common/toast/Toast';
import { removeExtraSpace } from '../../../utils/utils';
import { Module } from '../../roles/rolePermission/RolePermission';

export interface Option {
    value: string;
    label: string;
}
export interface onSuccessProps {
    message: string;
    statusCode: number;
    token: string;
    user: UserProps;
}
interface UserProps {
    email: string;
    fullName: string;
    id: string;
    image_url: string;
}

export interface DestinationUrlState {
    destinationUrl: string;
}

export const hasPermission = (moduleName: string, subModuleName: string, permissionName: string): boolean => {
    const permissions = useSelector((state: IRootState) => state.permissionsReducer?.data);
    if (!permissions) return false;

    return (
        permissions?.length &&
        permissions?.some(
            (module: Module) =>
                module.name === moduleName &&
                module.subModules.some(
                    (subModule) =>
                        subModule.name === subModuleName &&
                        subModule.permissions.some((permission) => permission.name === permissionName && permission.isAllowed),
                ),
        )
    );
};

const Login = () => {
    const location = useLocation();
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const [loginDetails, setLoginDetails] = useState({
        email: '',
        password: '',
    });
    const [showPass, setShowPass] = useState(false);
    const [unvalid, setValidation] = useState(false);
    const emailRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    const loginError = useSelector((state: IRootState) => state.authReducer.loginError);
    const loading = useSelector((state: IRootState) => state.authReducer.loading);
    const { data: permissionsData, noPermission: permissionNotAllowed } = useSelector((state: IRootState) => state.permissionsReducer);
    const appContext = useContext(AppContext);

    const [searchParams, setSearchParams] = useSearchParams();
    let invitedEmail = searchParams.get('email');
    if (invitedEmail) {
        invitedEmail = invitedEmail.trim().replace(/ /g, '+');
    }
    const invitedLocationId = searchParams.get('locationId');
    const invitedAdminId = searchParams.get('adminId');
    const invitedStaffCode = searchParams.get('staffCode');

    const useHasPermission = (moduleName: string, subModuleName: string, permissionName: string): boolean => {
        if (!permissionsData) return false;

        return (
            permissionsData?.length &&
            permissionsData?.some(
                (module: Module) =>
                    module.name === moduleName &&
                    module.subModules.some(
                        (subModule) =>
                            subModule.name === subModuleName &&
                            subModule.permissions.some((permission) => permission.name === permissionName && permission.isAllowed),
                    ),
            )
        );
    };

    useEffect(() => {
        if (permissionsData && permissionsData?.length && !permissionNotAllowed) {
            // Use same permission as sidebar: Dashboard tab is shown when "Property Dashboard" read is allowed
            const canReadPropertyDashboard = useHasPermission('Property Dashboard', 'Dashboard', 'read');
            const canReadProperty = useHasPermission('Properties & Units', 'Property', 'read');
            const canReadAvailability = useHasPermission('Self-Guided Tours', 'Availability', 'read');
            const canReadAppointment = useHasPermission('Self-Guided Tours', 'Appointments', 'read');
            const canReadVisitor = useHasPermission('Visitors', 'Visitor', 'read');
            const canReadMetrics = useHasPermission('Metrics', 'Metric', 'read');
            const canReadReports = useHasPermission('Property Report', 'Report', 'read');
            const canViewAmenities = useHasPermission('Amenity', 'Amenities', 'read');
            const canDelete = useHasPermission('User Management', 'Users Management', 'delete');
            const canSetRolesAndPermissions = useHasPermission('Roles and Permissions', 'Roles', 'read');
            const canManageStaff = useHasPermission('Staff Management', 'Staff', 'read');
            const canSupportTicket = useHasPermission('Support Tickets', 'Support Ticket', 'read');

            if (canReadPropertyDashboard) {
                navigate('/dashboard');
            } else if (canReadProperty) {
                navigate('/locations');
            } else if (canReadAppointment || canReadAvailability) {
                navigate('/visits');
            } else if (canReadVisitor) {
                navigate('/visitor');
            } else if (canReadMetrics) {
                navigate('/metrics');
            } else if (canReadReports) {
                navigate('/property-report');
            } else if (canViewAmenities) {
                navigate('/amenities');
            } else if (canDelete) {
                navigate('/user-management');
            } else if (canSetRolesAndPermissions) {
                navigate('/role-listing');
            } else if (canManageStaff) {
                navigate('/staff-management');
            } else if (canSupportTicket) {
                navigate('/support-ticket');
            } else {
                navigate('/no-permission');
            }
        }
        if (permissionNotAllowed) {
            navigate('/no-permission');
        }
    }, [permissionsData, permissionNotAllowed]);

    const { destinationUrl } = (useLocation().state as DestinationUrlState) || '';

    const userType = Roles.find((role: Option) => role.value === localStorage.getItem('type'))?.label;

    const searchSegment = location?.search || '';

    useEffect(() => {
        if (!userType) {
            return;
        }

        navigate(`/${searchSegment}`, { replace: true });
    }, [userType, searchSegment, navigate]);

    const handleLoginDetails = (event: React.FormEvent<HTMLInputElement>) => {
        event.preventDefault();

        const { name, value } = event.target as HTMLInputElement;

        setLoginDetails({
            ...loginDetails,
            [name]: removeExtraSpace(value),
        });
    };

    useEffect(() => {
        if (!!searchParams && !!invitedEmail) {
            setLoginDetails((loginDetails) => {
                return {
                    ...loginDetails,
                    email: (invitedEmail && invitedEmail) || '',
                };
            });
        }
    }, [searchParams, invitedEmail]);

    const handleSignIn = (e: React.FormEvent) => {
        e.preventDefault();
        if (loginDetails.email.length === 0 || loginDetails.password.length === 0 || loginDetails.password.length > 15) {
            setValidation(true);
            emailRef.current?.focus();
            loginDetails.email.length !== 0 && passwordRef.current?.focus();
            return;
        } else {
            unvalid && setValidation(false);
            dispatch(getAuth({ ...loginDetails, loginTimestamp: Math.floor(Date.now() / 1000) }, onSuccess));
        }
    };

    const onSuccess = async (data: onSuccessProps) => {
        appContext.adminAccessToken = data.token;
        appContext.userInfo = data.user;
        if (invitedAdminId && invitedEmail && invitedLocationId && invitedStaffCode) {
            navigate(`/invitation${location?.search}`);
        }
    };

    useEffect(() => {
        dispatch(resetValidationMessage());
    }, []);

    return (
        <>
            {loginError && <Toast message={loginError} />}
            {loading && <Loader />}
            <div className={styles.contentCenter}>
                <div className={styles.loginWrapper}>
                    <div className={styles.loginForm}>
                        <form onSubmit={handleSignIn}>
                            <div className={styles.formWrapper}>
                                <label>Email</label>
                                <div className={styles.passwordWrapper}>
                                    <input
                                        name="email"
                                        value={loginDetails.email}
                                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                                        onChange={handleLoginDetails}
                                        placeholder="Enter your email address"
                                        autoFocus
                                        // readOnly={!!invitedEmail}
                                        // disabled={!!invitedEmail}
                                        ref={emailRef}
                                    />
                                </div>
                                {loginDetails.email.length === 0 && unvalid && (
                                    <span className={styles.invalidCred}>email should not be empty</span>
                                )}
                            </div>
                            <div className={styles.formWrapper}>
                                <label>Password</label>
                                <div className={styles.passwordWrapper}>
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        name="password"
                                        value={loginDetails.password}
                                        onChange={handleLoginDetails}
                                        placeholder="Enter your password"
                                        ref={passwordRef}
                                    />
                                    <div className={styles.eyeToggler} onClick={() => setShowPass(!showPass)}>
                                        <img src={!showPass ? eyeClose : eyeOpen} alt="Eye" />
                                    </div>
                                </div>
                                {loginDetails.password.length > 15 && unvalid && (
                                    <span className={styles.invalidCred}>password must be shorter than or equal to 15 characters</span>
                                )}
                                {loginDetails.password.length === 0 && unvalid && (
                                    <span className={styles.invalidCred}>password should not be empty</span>
                                )}
                                <Link to="/forgot-password">Trouble signing in?</Link>
                            </div>
                            <div className={styles.formSubmit}>
                                <button type="submit">Sign In</button>
                            </div>
                        </form>
                        <div className={styles.leadSignup}>
                            <p>
                                Don’t have an account?
                                <Link to={location?.search ? `signup/${location.search}` : '/signup'}>Sign Up</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
